import {
	Box,
	Button,
	Checkbox,
	ColumnLayout,
	Container,
	FormField,
	Header,
	Input,
	Select,
	SpaceBetween,
	Tiles
} from '@cloudscape-design/components'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'

import CONSTANTS from '../../../../constants'
import { ApprovedGcpProjectWithTags } from '../../../../hooks/query/useApprovedGcpProjectsWithTags'
import {
	buildApplicationTargetOptions,
	buildApplicationTargetPrefill,
	getActiveApplicationTargetOption,
	getApplicationTargetDerivedResetValues,
	getDisplayValue,
	getSelectionValue,
	normalizeApplicationViewers,
	validateApplicationViewers
} from '../../../../helper/applicationDetailsHelpers'
import {
	findApprovedApplicationAliasConflict
} from '../../../../helper/gcpAliasValidation'
import { useNewApplicationFormStore } from '../../../../stores/applications/newApplicationFormStore'
import type { Platform } from '../../../../types/platforms'
import { StepConfig } from '../../steps_config'

type ApplicationDetailsProps = {
	registerValidation?: (validate: () => Promise<boolean>) => void
	applications?: Array<any>
	isApplicationsLoading?: boolean
	isApplicationsError?: boolean
	gcpProjects?: ApprovedGcpProjectWithTags[]
	isGcpProjectsLoading?: boolean
	platforms?: Platform[]
	isPlatformsLoading?: boolean
}

const applicationDetailFields: Array<StepConfig> = [
	{
		label: "Data Classification",
		fieldName: "dataClassification",
		options: CONSTANTS.DATA_CLASSIFICATION_LIST,
		type: "text"
	},
	{
		label: "Tier Level",
		fieldName: "tierLevel",
		options: CONSTANTS.TIER_LEVEL_OPTIONS,
		type: "text"
	}
]

const ApplicationDetails: FC<ApplicationDetailsProps> = ({
	registerValidation,
	applications,
	gcpProjects,
	platforms,
	isApplicationsLoading,
	isApplicationsError,
	isGcpProjectsLoading,
	isPlatformsLoading
}) => {
	const {
		formValues,
		actions: { setFormValues }
	} = useNewApplicationFormStore()

	const { control, trigger, setValue, setError, clearErrors, watch } = useForm({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues: formValues
	})

	// Filter the APPROVED CLUSTERS ONLY
	const approvedClusters = useMemo(
		() =>
			((platforms ?? []) as Platform[]).filter(
				(platform) => platform.status === 'APPROVED'
			),
		[platforms]
	)

	//Get the list of GCP Projects 
	const projectOptions = useMemo(
		() => buildApplicationTargetOptions(gcpProjects ?? [], approvedClusters),
		[gcpProjects, approvedClusters]
	)

	const selectedProjectID = getSelectionValue(formValues.gcpProjectID)

	const activeProjectOption = useMemo(
		() =>
			getActiveApplicationTargetOption(projectOptions, formValues.gcpProjectID),
		[projectOptions, formValues.gcpProjectID]
	)
	const isTargetDataLoading = isGcpProjectsLoading || isPlatformsLoading
	const watchedApplicationAlias = watch('applicationAlias')
	const resolvedApplicationEnvironment = formValues.environment

	const hasApplicationAliasAndResolvedEnvironment = Boolean(formValues.applicationAlias && formValues.environment)

	let applicationAliasValidationMessage: string | undefined

	if (hasApplicationAliasAndResolvedEnvironment) {
		if (isApplicationsLoading || isApplicationsError || !applications) {
			applicationAliasValidationMessage =
				'Unable to validate alias uniqueness right now'
		} else {
			const conflict = findApprovedApplicationAliasConflict(applications,watchedApplicationAlias,resolvedApplicationEnvironment)

			if (conflict) {
				applicationAliasValidationMessage = `Application Alias already exists for an approved application in ${conflict.environmentLabel}.`
			}
		}
	}

	const appDetails = [
		{
			label: 'GCP Project',
			value: getDisplayValue(formValues.gcpProjectID)
		},
		// {
		// 	label: 'Application Alias',
		// 	value: formValues.applicationAlias
		// },
		...(formValues.gcpProjectID?.resolution?.status === "ready" ? 
			[
				{
					label: 'Infrastructure Type',
					value: !formValues.gcpProjectID?.resolution?.supportsGke
						? 'Non-GKE'
						: formValues.infrastructureType
						? formValues.infrastructureType === 'gke'
							? 'GKE'
							: 'Non-GKE'
						: ''
				},
		] : []),
		{
			label: 'Environment',
			value: getDisplayValue(formValues.environment)
		},
		{
			label: 'Deployment',
			value: getDisplayValue(formValues.deployment)
		},
		{
			label: 'Portfolio',
			value: getDisplayValue(formValues.portfolio)
		},
		{
			label: 'Platform Owner',
			value: formValues.platformOwner
		},
		{
			label: 'Platform Owner Email',
			value: formValues.platformOwnerEmail
		},
		...(formValues.infrastructureType === 'gke' && formValues.gcpProjectID?.resolution?.resolvedCluster
			? [
					{
						label: 'GKE Workload Cluster',
						value: formValues.gcpProjectID?.resolution?.resolvedCluster?.namePrefix
					}
			  ]
			: []
		)
	]

	const setTargetValues = useCallback(
		(values: Record<string, any>) => {
			Object.entries(values).forEach(([fieldName, value]) => {
				setValue(fieldName, value)
				setFormValues(fieldName, value)
			})
		},
		[setFormValues, setValue]
	)

	const applySelectedGcpProject = (gcpProjectID?: string) => {
		setTargetValues(getApplicationTargetDerivedResetValues())

		const selectedOption = projectOptions.find(
			(option) => option.value === gcpProjectID
		)

		if (!selectedOption || selectedOption.resolution.status !== 'ready') {
			return
		}

		setTargetValues(buildApplicationTargetPrefill(selectedOption.resolution))
	}

	useEffect(() => {
		if (isTargetDataLoading || !selectedProjectID || activeProjectOption) {
			return
		}

		setTargetValues(getApplicationTargetDerivedResetValues())
	}, [
		activeProjectOption,
		isTargetDataLoading,
		selectedProjectID,
		setTargetValues
	])

	const validateApplicationDetails = useCallback(async () => {
		const isSelectedTargetReady = Boolean(
			getActiveApplicationTargetOption(projectOptions, formValues.gcpProjectID)
		)
		const fieldsToValidate = [
			'gcpProjectID',
			'deployment',
			'dataClassification',
			'tierLevel',
			'applicationName',
			'applicationAlias',
			'applicationViewer',
			'applicationOwner',
			'applicationOwnerEmail'
		]

		if (formValues.gcpProjectID?.resolution?.supportsGke) {
			fieldsToValidate.push('infrastructureType')
		}

		const fieldsValid = await trigger(fieldsToValidate)

		if (!isSelectedTargetReady) {
			setTargetValues(getApplicationTargetDerivedResetValues())
			setError('gcpProjectID', {
				type: 'validate',
				message: 'Please select an available GCP Project'
			})
			return false
		}

		if (applicationAliasValidationMessage) {
			return false
		}

		clearErrors('gcpProjectID')
		return fieldsValid
	}, [
		applicationAliasValidationMessage,
		clearErrors,
		formValues.gcpProjectID,
		projectOptions,
		setError,
		setTargetValues,
		trigger
	])

	useEffect(() => {
		registerValidation?.(validateApplicationDetails)
	}, [registerValidation, validateApplicationDetails])

	return (
		<Container>
			<SpaceBetween direction="vertical" size="l">
				<Controller
					name="applicationName"
					control={control}
					rules={{
						required: 'Please enter an application name',
						pattern: {
							value: /^[a-z]+(?:-[a-z]+)*$/,
							message:
								'Application name must use kebab-case: lowercase letters separated by hyphens.'
						}
					}}
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<FormField
							label="Application Name"
							description="Enter the name of the application. Use kebab-case with lowercase letters and hyphens, for example atlas-orders-dev."
							errorText={error?.message}
							stretch
						>
							<Input
								placeholder="application-name"
								value={value ?? ''}
								onChange={({ detail }) => {
									onChange(detail.value)
									setFormValues('applicationName', detail.value)
								}}
							/>
						</FormField>
					)}
				/>
				<Controller
					name="applicationAlias"
					control={control}
					rules={{
						required: 'Please enter Application Alias',
						minLength: {
							value: 5,
							message: 'Minimum length is 5 characters'
						},
						maxLength: {
							value: 5,
							message: 'Maximum length is 5 characters'
						},
						pattern: {
							value: /^[a-z0-9-]+$/,
							message: 'Application alias should be lowercase only'
						}
					}}
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<FormField
							label="Application Alias"
							description="Alias for the resources to be provisioned."
							errorText={applicationAliasValidationMessage ?? error?.message}
							stretch
						>
							<Input
								placeholder="alias"
								value={value ?? ''}
								onChange={({ detail }) => {
									onChange(detail.value)
									setFormValues('applicationAlias', detail.value)
								}}
							/>
						</FormField>
					)}
				/>
				<Controller
					name="gcpProjectID"
					control={control}
					rules={{ required: 'Please select a GCP Project' }}
					render={({ field: { onChange, value }, fieldState: { error } }) => (
						<FormField
							label="GCP Project"
							description="Select the GCP Project where this workload will reside."
							errorText={error?.message}
							stretch
						>
							<Select
								placeholder="Select GCP Project"
								filteringType="auto"
								filteringPlaceholder="Search GCP Projects"
								statusType={isTargetDataLoading ? 'loading' : 'finished'}
								options={projectOptions}
								selectedOption={value ?? null}
								onChange={({ detail }) => {
									const selectedOption = detail.selectedOption
									onChange(selectedOption)
									setFormValues('gcpProjectID', selectedOption)
									applySelectedGcpProject(selectedOption.value)
								}}
							/>
						</FormField>
					)}
				/>
				{formValues.gcpProjectID?.resolution?.status === 'ready' &&
				formValues.gcpProjectID?.resolution?.supportsGke ? 
				(<>
					<Controller
					name='infrastructureType'
					control={control}
					rules={{required: 'Please select an infrastructure type for your application'}}
					render={({field: {onChange, value}, fieldState: {error}}) => (
						<FormField
						label="Infrastructure Type"
						description="Select the infrastructure type that fits your application"
						errorText={error?.message}
						stretch
						>
							<Tiles
								value={value ?? ""}
								items={[
									{
										label: "GKE",
										value: "gke",
										description:
											"Deploy the application to an existing GKE workload in the selected GCP Project.",
										disabled: !formValues.gcpProjectID.resolution.resolvedCluster
									},
									{
										label: "Non-GKE",
										value: "non-gke",
										description:
											"Deploy the application directly in the selected GCP Project without using GKE."
									}
								]}
								onChange={({detail}) => {
									onChange(detail.value)
									setFormValues('infrastructureType', detail.value)
								}}
							/>
						</FormField>
					)}/>
				</>) : (<></>)}
				<ColumnLayout columns={2} variant="default">
				{applicationDetailFields.map((field: StepConfig) => {
					return(
						<Controller
						name={field.fieldName}
						control={control}
						rules={{ required: `Please select ${field.label}` }}
						render={({
							field: { onChange, value },
							fieldState: { error }
						}) => (
							<FormField
								label={field.label}
								errorText={error?.message}
								stretch
							>
								<Select
									placeholder={`Select ${field.label}`}
									options={field.options}
									selectedOption={value ?? null}
									onChange={({ detail }) => {
										onChange(detail.selectedOption)
										setFormValues(field.fieldName, detail.selectedOption)
									}}
								/>
							</FormField>
						)}
					/>
					)
				})}
				</ColumnLayout>
				<ColumnLayout columns={2} variant='default'>
					<Controller
						name="applicationOwner"
						control={control}
						rules={{
							required: 'Please enter the owner of the application',
							pattern: {
								value: /^[a-z]+(?:-[a-z]+)*$/,
								message:
									'Application owner name must use kebab-case: lowercase letters separated by hyphens.'
							}
						}}
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<FormField
								label="Application Owner Name"
								description="Use kebab-case, e.g. juan-cruz"
								errorText={error?.message}
								stretch
							>
								<Input
									placeholder="application-owner-name"
									value={value ?? ''}
									onChange={({ detail }) => {
										onChange(detail.value)
										setFormValues('applicationOwner', detail.value)
									}}
								/>
							</FormField>
						)}
					/>
					<Controller
						name="applicationOwnerEmail"
						control={control}
						rules={{
							required: 'Please enter the email of application owner',
							pattern: {
								value: /^[^\s@]+@globe\.com\.ph$/,
								message:
									'Invalid Application Owner Email Address.'
							}
						}}
						render={({ field: { onChange, value }, fieldState: { error } }) => (
							<FormField
								label="Application Owner Email"
								description="Example: juan.cruz@globe.com.ph"
								errorText={error?.message}
								stretch
							>
								<Input
									placeholder="juan-cruz@globe.com.ph"
									value={value ?? ''}
									onChange={({ detail }) => {
										onChange(detail.value)
										setFormValues('applicationOwnerEmail', detail.value)
									}}
								/>
							</FormField>
						)}
					/>
				</ColumnLayout>
				<Controller
					name="applicationViewer"
					control={control}
					rules={{ validate: validateApplicationViewers }}
					render={({ field: { onChange, value }, fieldState: { error } }) => {
						const applicationViewers = normalizeApplicationViewers(value)
						const viewerInputs =
							applicationViewers.length > 0 ? applicationViewers : ['']

						return (
							<FormField
								label="Application Viewer(s)"
								description="Email addresses for viewer-level permissions. Add one email address per row."
								errorText={error?.message}
								stretch
							>
								<SpaceBetween direction="vertical" size="xs">
									{viewerInputs.map((email, index) => (
										<div
											key={index}
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '8px'
											}}
										>
											<div style={{ flex: 1 }}>
												<Input
													name={`applicationViewer-${index}`}
													placeholder="juan.cruz@globe.com.ph"
													value={email}
													onChange={({ detail }) => {
														const list = normalizeApplicationViewers(value)
														const next = [...list]
														next[index] = detail.value
														onChange(next)
														setFormValues('applicationViewer', next)
													}}
												/>
											</div>
											<Button
												variant="link"
												iconName="remove"
												ariaLabel={`Remove application viewer ${index + 1}`}
												onClick={() => {
													const next = normalizeApplicationViewers(
														value
													).filter((_, viewerIndex) => viewerIndex !== index)
													onChange(next)
													setFormValues('applicationViewer', next)
												}}
											/>
										</div>
									))}
									<Button
										iconName="add-plus"
										onClick={() => {
											const list = normalizeApplicationViewers(value)
											const next = list.length > 0 ? [...list, ''] : ['', '']
											onChange(next)
											setFormValues('applicationViewer', next)
										}}
									>
										Add
									</Button>
								</SpaceBetween>
							</FormField>
						)
					}}
				/>
				<Container header={<Header variant="h3">Add-ons</Header>}>
					<SpaceBetween direction="vertical" size="s">
						<Checkbox
							checked={formValues.addOns?.mongodb === true}
							onChange={({ detail }) =>
								setFormValues('addOns', {
									...formValues.addOns,
									mongodb: detail.checked
								})
							}
						>
							MongoDB
						</Checkbox>

						<Checkbox
							checked={formValues.addOns?.confluent === true}
							onChange={({ detail }) =>
								setFormValues('addOns', {
									...formValues.addOns,
									confluent: detail.checked
								})
							}
						>
							Confluent
						</Checkbox>
					</SpaceBetween>
				</Container>
				<Container header={<Header variant="h3">Application Details</Header>}>
					<ColumnLayout columns={2} variant="text-grid">
						{appDetails.map((detail) => (
							<div key={detail.label}>
								<Box variant="awsui-key-label">{detail.label}</Box>
								<div>{detail.value || '-'}</div>
							</div>
						))}
					</ColumnLayout>
				</Container>
			</SpaceBetween>
		</Container>
	)
}

export default ApplicationDetails
